import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import * as RoleDefaults from '@revelio/data-access/role-defaults';

import type { TreeFilter } from '../types/filters';

// Only the GENERATED_DEFAULT_CUSTOM_ROLE_* arrays are selectable defaults;
// exclude the GENERATED_ROLE_DEFAULTS_VERSION string stamp.
type RoleDefaultsKeys = Exclude<
  keyof typeof RoleDefaults,
  'GENERATED_ROLE_DEFAULTS_VERSION'
>;
type RoleSelections = TreeFilter['selections'];

/**
 * Resolve the generated default ROLE selections for a given taxonomy selection.
 * Falls back to the global default when selection is { default: true } or unrecognized.
 */
export function getRoleDefaultSelectionsForTaxonomy(
  selection: CustomRoleTaxonomySelection | undefined
): TreeFilter['selections'] {
  if (!selection || 'default' in selection) {
    // biome-ignore lint/performance/noDynamicNamespaceImportAccess: migrated from develop
    return RoleDefaults['GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT'] ?? [];
  }

  // Handle explicit custom taxonomyId (e.g., 61 ONET, 62 ESCO, 63 LIGHTCAST)
  if (
    'taxonomyId' in selection &&
    typeof selection.taxonomyId !== 'undefined'
  ) {
    const idNum = Number(selection.taxonomyId);
    // Keep in sync with the generator's formatTaxonomyName(): CUSTOM_<id>
    const nameSegment = `CUSTOM_${idNum}`;
    const key =
      `GENERATED_DEFAULT_CUSTOM_ROLE_${nameSegment}` as RoleDefaultsKeys;
    // biome-ignore lint/performance/noDynamicNamespaceImportAccess: migrated from develop
    return RoleDefaults[key] ?? [];
  }

  if ('ricsK10Id' in selection && selection.ricsK10Id) {
    const key = `GENERATED_DEFAULT_CUSTOM_ROLE_RICS_K10_${Number(
      selection.ricsK10Id
    )}` as RoleDefaultsKeys;
    // biome-ignore lint/performance/noDynamicNamespaceImportAccess: migrated from develop
    return RoleDefaults[key] ?? [];
  }

  if ('ricsK50Id' in selection && selection.ricsK50Id) {
    const key = `GENERATED_DEFAULT_CUSTOM_ROLE_RICS_K50_${Number(
      selection.ricsK50Id
    )}` as RoleDefaultsKeys;
    // biome-ignore lint/performance/noDynamicNamespaceImportAccess: migrated from develop
    return RoleDefaults[key] ?? [];
  }

  return (
    (RoleDefaults as Record<RoleDefaultsKeys, RoleSelections>)[
      'GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT' as RoleDefaultsKeys
    ] ?? []
  );
}
